import React from "react";
import DOMPurify from "dompurify";
import { Grid, Table, TableBody, TableCell, TableRow } from "@mui/material";

const CaseStudies = ({ projects, primaryColor }) => {
  const caseStudies = projects || [];
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

  // Helper to check if a URL is a YouTube or Vimeo link
  const getEmbedTypeAndUrl = (url) => {
    if (!url) return { type: null, embedUrl: null };
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
    if (ytMatch && ytMatch[1]) {
      return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}` };
    }
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch && vimeoMatch[1]) {
      return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
    }
    return { type: 'video', embedUrl: url };
  };

  const renderExtraFields = (study) => {
    const formatEuro = (value) => {
      if (!value) return '';
      const num = parseFloat(String(value).replace(/[^\d.,-]/g, '').replace(',', ''));
      if (isNaN(num)) return value;
      return `€${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const cells = [
      study.panels
        ? { label: "Panels", value: study.panels }
        : null,
      study.costAfterGrant
        ? { label: "Cost After Grant", value: formatEuro(study.costAfterGrant) }
        : null,
      study.annualSavings
        ? { label: "Annual Savings", value: formatEuro(study.annualSavings) }
        : null,
      study.paybackPeriod
        ? { label: "Payback Period", value: `${parseInt(study.paybackPeriod, 10)} years` }
        : null,
    ].filter(Boolean);

    if (cells.length === 0) return null;

    if (isMobile) {
      // Vertical layout for mobile
      return (
        <div style={{ marginBottom: '24px' }}>
          <Table size="small" style={{ marginBottom: '0', maxWidth: 700 }}>
            <TableBody>
              {cells.map((cell, idx) => (
                <TableRow key={idx}>
                  <TableCell style={{ borderBottom: 0, fontWeight: 'bold', fontSize: '0.95em', width: '50%', padding: '8px 8px 0 0' }}>{cell.label}</TableCell>
                  <TableCell style={{ borderBottom: 0, width: '50%', padding: '8px 8px 0 0' }}>{cell.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    } else {
      // Horizontal layout for desktop
      while (cells.length < 4) {
        cells.push({ label: "", value: "" });
      }
      return (
        <div style={{ marginBottom: '24px' }}>
          <Table size="small" style={{ marginBottom: '0', maxWidth: 700 }}>
            <TableBody>
              <TableRow>
                {cells.map((cell, idx) => (
                  <TableCell key={idx} style={{ borderBottom: 0, width: '25%', verticalAlign: 'top', padding: '8px 8px 0 0' }}>
                    {cell.label && (
                      <span style={{ fontWeight: 'bold', display: 'block', fontSize: '0.95em' }}>{cell.label}</span>
                    )}
                    <span>{cell.value}</span>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      );
    }
  };

  return (
    <Grid container style={{ padding: isMobile ? '60px 30px' : '60px 60px', width: '100%' }}>
      <Grid size={12}>
        <h3 style={{ margin: 0, marginBottom: 20, fontSize: "26px", color: "#000" }}>
          Case Studies
        </h3>
      </Grid>
      <Grid size={12} style={{ marginTop: "20px" }}>
        <Grid container spacing={4}>
          {caseStudies.map((study, index) => (
            <React.Fragment key={study.projectId}>
              <Grid size={12}>
                {study.type === 'Video' ? (
                  isMobile ? (
                    <Grid container style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                      <Grid item size={12} style={{ paddingLeft: 0, paddingBottom: 16 }}>
                        <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                          {study.category}
                        </h3>
                        <p style={{ paddingBottom: '14px', marginBottom: 0 }}>
                          {study.county && (
                            <>
                              <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                              {study.county}
                            </>
                          )}
                        </p>
                        {renderExtraFields(study)}
                        <div>
                          {study.description
                            ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                            : null}
                        </div>
                      </Grid>
                      <Grid item size={12} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {(() => {
                          const { type, embedUrl } = getEmbedTypeAndUrl(study.image);
                          return (
                            <div style={{
                              position: 'relative',
                              width: '100%',
                              paddingTop: '56.25%' // 16:9 aspect ratio
                            }}>
                              {type === 'youtube' || type === 'vimeo' ? (
                                <iframe
                                  src={embedUrl}
                                  title={study.description || (type === 'youtube' ? 'YouTube video' : 'Vimeo video')}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0
                                  }}
                                />
                              ) : (
                                <video
                                  src={embedUrl}
                                  controls
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '2px',
                                    border: 0
                                  }}
                                >
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          );
                        })()}
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container style={{ display: "flex", overflow: "hidden", flexWrap: 'nowrap' }}>
                      <Grid item size={{ xs: 12, md: 6 }} style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                        {(() => {
                          const { type, embedUrl } = getEmbedTypeAndUrl(study.image);
                          // Use 533px width for video, 300px for image
                          const mediaWidth = (study.type === 'Video') ? 500 : 300;
                          return (
                            <div style={{
                              position: 'relative',
                              width: mediaWidth,
                              maxWidth: mediaWidth,
                              height: 0,
                              paddingTop: '56.25%' // 16:9 aspect ratio
                            }}>
                              {type === 'youtube' || type === 'vimeo' ? (
                                <iframe
                                  src={embedUrl}
                                  title={study.description || (type === 'youtube' ? 'YouTube video' : 'Vimeo video')}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0
                                  }}
                                />
                              ) : (
                                <video
                                  src={embedUrl}
                                  controls
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '2px',
                                    border: 0
                                  }}
                                >
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          );
                        })()}
                      </Grid>
                      <Grid item size={{ xs: 12, md: 8 }} style={{
                        paddingLeft: "20px",
                        minWidth: 0,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}>
                        <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                          {study.category}
                        </h3>
                        <p style={{ paddingBottom: '14px', marginBottom: 0 }}>
                          {study.county && (
                            <>
                              <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                              {study.county}
                            </>
                          )}
                        </p>
                        {renderExtraFields(study)}
                        <div style={{ flexGrow: 1 }}>
                          {study.description
                            ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                            : null}
                        </div>
                      </Grid>
                    </Grid>
                  )
                ) : isMobile ? (
                  <Grid container style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Grid item size={12} style={{ paddingLeft: 0, paddingBottom: 16 }}>
                      <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                        {study.category}
                      </h3>
                      <p style={{ paddingBottom: '14px', marginBottom: 0 }}>
                        {study.county && (
                          <>
                            <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                            {study.county}
                          </>
                        )}
                      </p>
                      {renderExtraFields(study)}
                      <div>
                        {study.description
                          ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                          : null}
                      </div>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                      <img
                        src={study.image}
                        alt={study.description}
                        style={{
                          objectFit: "contain",
                          borderRadius: "2px",
                          maxWidth: '100%',
                          height: 'auto',
                          display: 'block',
                          marginBottom: '30px'
                        }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container style={{ display: "flex", overflow: "hidden", flexWrap: 'nowrap' }}>
                    <Grid item size={{ xs: 12, md: 4 }} style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minWidth: 0 }}>
                      <img
                        src={study.image}
                        alt={study.description}
                        style={{
                          objectFit: "contain",
                          marginRight: "20px",
                          borderRadius: "2px",
                          maxWidth: '100%',
                          height: 'auto',
                          display: 'block',
                          marginBottom: 0
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, md: 8 }} style={{ paddingLeft: "20px", minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                        {study.category}
                      </h3>
                      <p style={{ paddingBottom: '14px', marginBottom: 0 }}>
                        {study.county && (
                          <>
                            <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                            {study.county}
                          </>
                        )}
                      </p>
                      {renderExtraFields(study)}
                      <div style={{ flexGrow: 1 }}>
                        {study.description
                          ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                          : null}
                      </div>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              {index < caseStudies.length - 1 && (
                <hr style={{ width: "100%", border: "none", borderTop: "1px solid #e0e0e0", margin: "20px 0" }} />
              )}
            </React.Fragment>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CaseStudies;
